import { DirectUpload } from '@rails/activestorage'
import { DIRECT_UPLOAD_URL, MAX_ALLOWED_BYTES } from '../../utilities/fileUtil'

export const IMAGE_SIGNED_ID_ATTR = 'data-signed-id'

const signedIdByPreviewSrc = new Map()

export function rememberImageSignedId (previewSrc, signedId) {
  if (previewSrc && signedId) {
    signedIdByPreviewSrc.set(previewSrc, signedId)
  }
}

export function getImageSignedId (previewSrc) {
  return signedIdByPreviewSrc.get(previewSrc)
}

function eachImage (root, callback) {
  if (!root) return
  if (root.nodeName === 'IMG') callback(root)
  if (typeof root.querySelectorAll === 'function') {
    root.querySelectorAll('img').forEach(callback)
  }
}

export function replaceBlobUrisWithDataUrls (editor, root) {
  if (!root || !editor) return

  const blobCache = editor.editorUpload?.blobCache

  eachImage(root, (img) => {
    const src = img.getAttribute('src') || ''
    if (!src.startsWith('blob:')) return

    const blobInfo = blobCache?.getByUri?.(src)
    const base64 = typeof blobInfo?.base64 === 'function' ? blobInfo.base64() : null
    if (!base64) return

    const type = blobInfo.blob()?.type || 'image/png'
    const dataUrl = `data:${type};base64,${base64}`
    const signedId = img.getAttribute(IMAGE_SIGNED_ID_ATTR) || getImageSignedId(src)
    if (signedId) {
      rememberImageSignedId(dataUrl, signedId)
      img.setAttribute(IMAGE_SIGNED_ID_ATTR, signedId)
    }
    img.setAttribute('src', dataUrl)
  })
}

function isImageBlob (blob) {
  return Boolean(blob?.type && blob.type.startsWith('image/'))
}

function toNamedImageFile (blob, filename) {
  const type = blob.type
  const extension = type.split('/')[1] || 'png'
  const name = filename || blob.name || `image.${extension}`

  if (blob instanceof File && blob.name) return blob
  return new File([blob], name, { type, lastModified: Date.now() })
}

export function uploadImageToStorage (blob, { filename, onProgress } = {}) {
  if (!blob) {
    return Promise.reject(new Error('No file selected'))
  }

  if (!isImageBlob(blob)) {
    return Promise.reject(new Error('File must be an image'))
  }

  const file = toNamedImageFile(blob, filename)
  if (file.size > MAX_ALLOWED_BYTES) {
    return Promise.reject(new Error('Image must be 10 MB or smaller'))
  }

  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, DIRECT_UPLOAD_URL, {
      directUploadWillStoreFileWithXHR (request) {
        if (typeof onProgress !== 'function') return
        request.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            onProgress((event.loaded / event.total) * 100)
          }
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        reject(error instanceof Error ? error : new Error('Failed to upload image'))
        return
      }
      if (!blob?.signed_id) {
        reject(new Error('Upload succeeded but no signedId was returned'))
        return
      }
      resolve(blob)
    })
  })
}

export function applySignedIdsToContent (html) {
  if (!html) return html

  const parser = new DOMParser()
  const document = parser.parseFromString(html, 'text/html')

  document.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src')
    const signedId = img.getAttribute(IMAGE_SIGNED_ID_ATTR) || getImageSignedId(src)
    if (!signedId) return
    img.setAttribute(IMAGE_SIGNED_ID_ATTR, signedId)
    img.setAttribute('src', signedId)
  })

  return document.body.innerHTML
}
